import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { deleteSelection } from 'prosemirror-commands';
import { NodeSelection } from 'prosemirror-state';
import { deleteTable } from 'prosemirror-tables';
import { findParentNode, findParentNodeOfType } from 'prosemirror-utils';

import { TFunction } from '@lyearn/i18n';
import { portalKey } from '@lyearn/lygo-core';

import { getIframeSrc } from '../../../components/EmbedPopover/utils';
import { AdvancedMenuPluginKey } from '../../../plugins/AdvancedMenuPlugin/utils';
import { WebBookmarkLayout } from '../../../types';
import {
  getCurrentSelectionNode,
  getElementPos,
  insertNode,
  isTable,
  selectionType,
  selectNode,
  setNodeAttrs,
  setPluginMeta,
  setSelection,
} from '../../../utils';
import { ReplaceInputComponent } from '../ReplaceInput';
import { clickFunction, enableFunction, MenuType, titleFunction } from '../types';
import { blockMenuKey, convertLinkNodes, getTextOnlyNodeTypes, hideBlockMenu } from './utils';

// Delete Menu item for all the blocks.
export const onDeleteClick: clickFunction = (_open, anchorEl, view) => {
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.inside !== undefined && elementPos.pos !== undefined) {
    setSelection(
      view,
      selectionType.NODE,
      elementPos.inside < 0 ? elementPos.pos : elementPos.inside,
    );
  }
  // check if the selected element is a table element
  const { schema, selection } = view.state;
  const parentTable = findParentNode((node) => isTable(node, schema))(selection);
  if (parentTable) {
    deleteTable(view.state, view.dispatch);
  }

  // check if the selected element is a list item
  const isNodeViewListNode = findParentNodeOfType([
    view.state.schema.nodes.toggle_list,
    view.state.schema.nodes.check_list,
  ])(view.state.selection);
  if (isNodeViewListNode) {
    const currentPos = view.state.selection.$from.pos;
    setSelection(view, selectionType.NODE, currentPos - 1);
  }

  deleteSelection(view.state, view.dispatch);
  hideBlockMenu(view);
  view.focus();
};

// Turrn Into Menu item for Text Nodes.
export const displayTurnIntoMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const elementPos = getElementPos(anchorEl, view);
    if (Number.isInteger(elementPos?.pos)) {
      const currentSelectionNode = getCurrentSelectionNode(view, elementPos!.pos);
      const textOnlyNodeTypes = getTextOnlyNodeTypes(view);
      if (currentSelectionNode && textOnlyNodeTypes.includes(currentSelectionNode.type.name)) {
        return true;
      }
    }
  }
  return false;
};

export const toggleTurnIntoMenu: clickFunction = (open, anchorEl, view) => {
  let blockMenuProps = {
    open: open,
    anchorEl: anchorEl,
    menuType: MenuType.TURN_INTO,
  };
  setPluginMeta(view, blockMenuKey, blockMenuProps);
};

// Caption Menu item for Media blocks.
export const toggleCaption: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.pos !== undefined) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    let newCaption = null;
    if (currentSelectionNode?.attrs.caption === null) {
      newCaption = '';
    }
    setNodeAttrs(view, elementPos.pos, {
      ...currentSelectionNode?.attrs,
      caption: newCaption,
    });
  }
  hideBlockMenu(view);
};

export const displayCaptionMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    if (
      currentSelectionNode &&
      // No types available for Nodetype to have a defaultAttrs prop.
      'caption' in (currentSelectionNode.type as any).defaultAttrs &&
      (currentSelectionNode.attrs.src || currentSelectionNode.attrs.href)
    ) {
      return true;
    }
  }
  return false;
};

export const displayCaptionMenuTitle: (t: TFunction) => titleFunction =
  (t: TFunction) => (open, anchorEl, view) => {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    if (
      currentSelectionNode &&
      // No types available for Nodetype to have a defaultAttrs prop.
      'caption' in (currentSelectionNode.type as any).defaultAttrs &&
      (currentSelectionNode.attrs.src || currentSelectionNode.attrs.href)
    ) {
      if (currentSelectionNode.attrs.caption === null) {
        return t('Lygo:AddCaption');
      } else {
        return t('Lygo:RemoveCaption');
      }
    }
    return '';
  };

// Duplicate Menu item for all the blocks.
export const onDuplicateClick: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const parentTable = findParentNode((node) => view.state.schema.nodes.table === node.type)(
    view.state.selection,
  );

  const nodeViewListNode = findParentNodeOfType([
    view.state.schema.nodes.toggle_list,
    view.state.schema.nodes.check_list,
  ])(view.state.selection);

  if (parentTable) {
    const insertParaPos = parentTable.pos + parentTable.node.nodeSize;
    insertNode(view, selectionType.NODE, parentTable.node, insertParaPos);
  } else if (nodeViewListNode) {
    const insertPos = nodeViewListNode.pos + nodeViewListNode.node.nodeSize;
    insertNode(view, selectionType.NODE, nodeViewListNode.node, insertPos);
  } else {
    insertNode(
      view,
      selectionType.NODE,
      (view.state.selection as NodeSelection).node,
      view.state.selection.to,
    );
  }
  hideBlockMenu(view);
};

// Replace Menu Item for Media blocks.
export const displayReplaceMenuItem: enableFunction = (_open, _anchorEl, view, selectedNode) => {
  const currentSelectionNode = selectedNode ?? (view.state.selection as NodeSelection).node;
  // config type in not available in EditorState of prosemirroe-model.
  const nodes = (view.state as any).config.schema.nodes;
  if (
    currentSelectionNode &&
    [nodes.video, nodes.audio, nodes.file, nodes.image, nodes.embed].includes(
      currentSelectionNode.type,
    ) &&
    (currentSelectionNode.attrs.src ||
      currentSelectionNode.attrs.url ||
      currentSelectionNode.attrs.href)
  ) {
    return true;
  }
  return false;
};

export const displayReplaceMenuItem_: enableFunction = (_open, _anchorEl, view, selectedNode) => {
  const currentSelectionNode = selectedNode ?? (view.state.selection as NodeSelection).node;
  // config type in not available in EditorState of prosemirroe-model.
  const nodes = (view.state as any).config.schema.nodes;
  if (
    currentSelectionNode &&
    [nodes.video, nodes.audio, nodes.file, nodes.image, nodes.embed].includes(
      currentSelectionNode.type,
    ) &&
    (currentSelectionNode.attrs.src ||
      currentSelectionNode.attrs.url ||
      currentSelectionNode.attrs.href)
  ) {
    return true;
  }
  return false;
};

const portalKeyId = nanoid();
export const onReplaceClick: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  const currentSelectionNode = (view.state.selection as NodeSelection).node;
  const _PortalAPI = portalKey.getState(view.state).portalAPI;
  // It will change whenever menu item is clicked and on basis of the
  // change in triggerClick property, useEffect will trigger the click
  // on the input element in the ReplaceInputComponent.
  const triggerClick = nanoid();
  _PortalAPI.createPortal(
    ReactDOM.createPortal(
      <ReplaceInputComponent
        pos={elementPos!.pos}
        selectedNode={currentSelectionNode}
        triggerClick={triggerClick}
        view={view}
      />,
      document.getElementById('block-menu-container')!,
      portalKeyId,
    ),
  );
  hideBlockMenu(view);
};

// Change the bookmark layout Menuitem for Bookmark block.
export const displayPreviewMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    if (
      currentSelectionNode &&
      currentSelectionNode.type === view.state.schema.nodes.webbookmark &&
      currentSelectionNode.attrs.url
    ) {
      return true;
    }
  }
  return false;
};

export const displayPreviewTitle: (t: TFunction) => titleFunction =
  (t: TFunction) => (_open, _anchorEl, view) => {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    return currentSelectionNode && currentSelectionNode.attrs.url
      ? currentSelectionNode.attrs.layout === WebBookmarkLayout.Small
        ? t('Lygo:BigPreview')
        : t('Lygo:SmallPreview')
      : '';
  };

export const onPreviewClick: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.pos !== undefined) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    setNodeAttrs(view, elementPos.pos, {
      ...currentSelectionNode?.attrs,
      layout:
        currentSelectionNode?.attrs.layout === WebBookmarkLayout.Small
          ? WebBookmarkLayout.Big
          : WebBookmarkLayout.Small,
    });
  }
  hideBlockMenu(view);
};

// Conversion between Embed and Bookmark Menuitem for Embed and Bookmark blocks.
export const displayConvertLinkMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    if (
      currentSelectionNode &&
      convertLinkNodes(view) &&
      (currentSelectionNode.attrs.url || currentSelectionNode.attrs.embedUrl)
    ) {
      return true;
    }
  }
  return false;
};

export const displayConvertLinkTitle: (t: TFunction) => titleFunction =
  (t: TFunction) => (_open, _anchorEl, view) => {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    return currentSelectionNode.attrs.url || currentSelectionNode.attrs.embedUrl
      ? currentSelectionNode.type === view.state.schema.nodes.webbookmark
        ? t('Lygo:Menu.embed.title')
        : t('Lygo:Menu.webbookmark.title')
      : '';
  };

export const onConvertLinkClick: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.pos !== undefined) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    view.dispatch(view.state.tr.deleteSelection());
    const convertUrl = currentSelectionNode?.attrs.url || currentSelectionNode?.attrs.embedUrl;
    insertNode(
      view,
      selectionType.NODE,
      currentSelectionNode.type === view.state.schema.nodes.webbookmark
        ? view.state.schema.nodes.embed.create({ embedUrl: getIframeSrc(convertUrl).parsedUrl })
        : view.state.schema.nodes.webbookmark.create({ url: convertUrl }),
      elementPos.pos,
    );
  }
  hideBlockMenu(view);
};

// Conversion between Image and Image Hotspot, Menuitem for Image and Image Hotspot blocks.
export const displayConvertImageMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    if (
      currentSelectionNode &&
      currentSelectionNode.type === view.state.schema.nodes.image &&
      currentSelectionNode.attrs.src
    ) {
      return true;
    }
  }
  return false;
};

export const displayConvertImageTitle: (t: TFunction) => titleFunction =
  (t: TFunction) => (_open, _anchorEl, view) => {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    return currentSelectionNode.attrs.src
      ? currentSelectionNode.attrs.hotspotsEnabled
        ? t('Lygo:Menu.imagehotspot.disable')
        : t('Lygo:Menu.imagehotspot.enable')
      : '';
  };

export const onConvertImageClick: clickFunction = (_open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.pos !== undefined) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    const attrs = currentSelectionNode?.attrs;
    setNodeAttrs(view, elementPos.pos, {
      ...attrs,
      hotspotsEnabled: !attrs.hotspotsEnabled,
    });
  }
  hideBlockMenu(view);
};

export const displaySettingMenuItem: enableFunction = (open, anchorEl, view) => {
  if (anchorEl && view && open) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    const state = AdvancedMenuPluginKey.getState(view.state);
    const advancedMenuItemsMap = state?.advancedMenuItemsMap;
    if (advancedMenuItemsMap?.get(currentSelectionNode?.type.name)) {
      return true;
    }
  }
  return false;
};

export const onSettingsClick: clickFunction = (open, anchorEl, view) => {
  selectNode(anchorEl, view);
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.pos !== undefined) {
    const currentSelectionNode = (view.state.selection as NodeSelection).node;
    const prevProps = AdvancedMenuPluginKey.getState(view.state);
    setPluginMeta(view, AdvancedMenuPluginKey, {
      ...prevProps,
      open: true,
      pos: elementPos.pos,
      currentSelectionNode,
      view,
    });
  }
  hideBlockMenu(view);
};
